<template>
	<div class="container__dropdowncheckboxfk">
		<label>{{ label }}</label>
		<div
			:ref="`ref__dropdowncheckboxfk__selectedoptions_${uniqueRef}`"
			class="container__dropdowncheckboxfk__selectedoptions"
			:class="{ disabled }"
			@click="clickDropdown"
		>
			<div
				v-if="activeOptions.length > 0"
				class="container__dropdowncheckboxfk__selectedoptions__tags"
			>
				<TagWithButton
					v-for="(opcao, index) in activeOptions"
					:key="index"
					button-icon="fas fa-times-circle"
					class-name="primary"
					:label="opcao[$t('name')]"
					:identificador-da-tag="opcao.id"
					@clickEvent="removeOption"
				/>
			</div>
			<div v-else>
				{{ emptyLabel }}
			</div>
		</div>
		<div
			:ref="`ref__dropdowncheckboxfk__dropdown_${uniqueRef}`"
			v-closable="{
				exclude: [
					`ref__dropdowncheckboxfk__dropdown_${uniqueRef}`,
					`ref__dropdowncheckboxfk__selectedoptions_${uniqueRef}`,
				],
				handler: 'closeDropdown',
			}"
			:class="['container__dropdowncheckboxfk__dropdown', { 'dropdown-open': dropdownOpen }]"
		>
			<div class="container__dropdowncheckboxfk__dropdown__filter">
				<input
					v-model="textFilter"
					type="text"
					class="container__dropdowncheckboxfk__dropdown__filter__input"
					:placeholder="$t('search')"
				/>
				<i class="fas fa-search icon" />
			</div>
			<div class="container__dropdowncheckboxfk__dropdown__options">
				<div
					v-for="opcao in dropdownOptions"
					v-show="dropdownFiltred.includes(opcao.id)"
					:key="opcao.id"
					:class="[
						'container__dropdowncheckboxfk__dropdown__options__item',
						{ 'option-active': isOptionActive(opcao.id) },
					]"
					@click="clickOption(opcao.id)"
				>
					<div class="container__dropdowncheckboxfk__dropdown__options__item__check">
						<Lottie
							:options="getAnimationOptions(opcao.id)"
							@animCreated="addAnimation"
						/>
					</div>
					<div>
						<div
							v-for="(value, propertieName, index) in opcao"
							:key="index"
						>
							<div
								v-if="!ignoreProperties.includes(propertieName)"
								class="container__dropdowncheckboxfk__dropdown__options__item__group__perpropertie"
							>
								<p
									class="container__dropdowncheckboxfk__dropdown__options__item__group__perpropertie__propname"
								>
									{{ propertieName }}
								</p>
								<p style="margin-bottom: 0">
									{{ value }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Lottie from '../Lottie/Lottie'
import TagWithButton from '../TagWithButton/TagWithButton'

import checkAnimationData from '../../assets/lottie/check'

import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

export default {
	components: { Lottie, TagWithButton },
	props: {
		/**
		 * Nenhum valor selecionado
		 */
		emptyLabel: { type: String, default: 'Nenhum valor selecionado' },
		/**
		 * Propriedades que nao devem ser exibidas na lista de opcoes
		 */
		ignoreProperties: { type: Array, default: () => [] },
		/**
		 * Lista com os itens marcados com check
		 */
		value: { type: Array, required: true },
		/**
		 * Label do componente
		 */
		label: String,
		/**
		 * Todas as opções disponiveis
		 */
		dropdownOptions: { type: Array, required: true },
		disabled: { type: Boolean, default: false },
	},
	data: () => {
		return {
			/**
			 * Configuracoes da animacao de check e animacao
			 */
			checkAnim: [],
			/**
			 * Muda se o dropdown esta aberto ou fechado
			 */
			dropdownOpen: false,
			/**
			 * Filtro para o dropdown
			 */
			textFilter: '',
		}
	},
	computed: {
		/**
		 * Opcoes que estao ativas no componente
		 */
		activeOptions() {
			return this.dropdownOptions.filter((option) => this.value?.includes(option.id))
		},

		/**
		 * Filtro do dropdown retorna apenas os Ids apos aplicar o filtro
		 */
		dropdownFiltred() {
			if (this.dropdownOptions.length > 0) {
				const textFilter = this.textFilter.toLowerCase()
				const columns = Object.keys(this.dropdownOptions[0]).filter((column) => column !== 'id')

				/**
				 * Realizar o filtro de acordo com o que foi digitado
				 */
				const result = this.dropdownOptions.filter((item) => {
					const model = _.pick(item, columns)
					const atualModel = Object.values(model).join(' ')
					return atualModel.toLowerCase().includes(textFilter)
				})

				return result.map((result) => result.id)
			}

			return []
		},

		/**
		 * Retorna um Guid para o componente ter como referencia
		 * um valor unico por componente
		 */
		uniqueRef() {
			return uuidv4()
		},
	},
	methods: {
		/**
		 * Adiciona a animacao quando lottie criar
		 */
		addAnimation(anim) {
			anim.setSpeed(3)
			this.checkAnim.push(anim)
		},
		/**
		 * Abrir e fechar o dropdown
		 */
		clickDropdown() {
			if (!this.disabled) {
				if (this.dropdownOpen) {
					this.closeDropdown()
				} else {
					this.value.forEach((id) => this.setLottieCheckToFinalFrame(id))
					this.dropdownOpen = true
				}
			}
		},
		/**
		 * Fecha o dropdown
		 */
		closeDropdown() {
			this.value.forEach((id) => this.setLottieCheckToInitialFrame(id))
			this.dropdownOpen = false
		},
		/**
		 * Remove ou adiciona opcao na lista com os valores de id dependendo
		 * se eles ja pertencem ou não a lista
		 */
		clickOption(id) {
			const index = this.value.indexOf(id)

			if (index < 0) {
				this.setLottieCheckToFinalFrame(id)
				this.value.push(id)
			} else {
				this.setLottieCheckToInitialFrame(id)
				this.value.splice(index, 1)
			}
		},
		/**
		 * Retorna configuracao para o lottie
		 */
		getAnimationOptions(id) {
			return {
				animationData: checkAnimationData,
				autoplay: false,
				loop: false,
				name: id,
			}
		},
		/**
		 * Retorna se a opcao está ativa
		 */
		isOptionActive(id) {
			return this.value.find((value) => value === id)
		},
		/**
		 * Remove um item selecionado na lista
		 */
		removeOption(id) {
			const index = this.value.indexOf(id)
			this.setLottieCheckToInitialFrame(id)
			this.value.splice(index, 1)
		},
		/**
		 * Seta a animacao do checkbox para o frame final
		 */
		setLottieCheckToFinalFrame(id) {
			const anim = this.checkAnim.find((anim) => anim.name === id)
			anim.playSegments([0, 91], true)
		},
		/**
		 * Seta a animacao do checkbox para o frame inicial
		 */
		setLottieCheckToInitialFrame(id) {
			const anim = this.checkAnim.find((anim) => anim.name === id)
			if (anim != undefined) anim.playSegments([35, 0], true)
		},
	},
}
</script>

<style scoped>
.container__dropdowncheckboxfk {
	position: relative;
	user-select: none;
	color: rgb(var(--color-primary));
	font-size: 0.75rem;
}

.container__dropdowncheckboxfk__selectedoptions {
	border: 1px solid var(--color-gray);
	border-radius: 4.5px;
	padding: 10.5px 16px;
	cursor: pointer;
}

.container__dropdowncheckboxfk__selectedoptions__tags {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.container__dropdowncheckboxfk__dropdown {
	position: absolute;
	z-index: 20;
	border-bottom-left-radius: 4.5px;
	border-bottom-right-radius: 4.5px;
	max-height: 0vh;
	width: 100%;
	overflow: hidden;
	margin-top: -3px;
	border-left: 1px solid var(--color-gray);
	border-right: 1px solid var(--color-gray);
	background-color: white;
	transition: max-height 0.2s ease-in-out;
}

.container__dropdowncheckboxfk__dropdown.dropdown-open {
	max-height: 30vh;
	border-bottom: 1px solid var(--color-gray);
}

.container__dropdowncheckboxfk__dropdown__filter {
	display: flex;
	align-items: center;
	border: 1px solid var(--color-gray);
	margin: 0.5rem;
	border-radius: 4.5px;
}

.container__dropdowncheckboxfk__dropdown__filter i {
	color: rgb(var(--color-default));
	margin-right: 0.5rem;
}

.container__dropdowncheckboxfk__dropdown__filter__input {
	border: none;
	width: 100%;
	height: 32px;
	font-size: 0.75rem;
	padding: 0.5rem;
	background-color: transparent;
	outline: none;
}

.container__dropdowncheckboxfk__dropdown__options {
	max-height: calc(30vh - 32px - 0.5rem);
	overflow-y: auto;
}

.container__dropdowncheckboxfk__dropdown__options__item {
	border-bottom: 1px solid var(--color-gray);
	padding: 0.5rem 1rem;
	cursor: pointer;
	display: flex;
	align-items: center;
}

.container__dropdowncheckboxfk__dropdown__options__item:hover {
	background-color: rgba(var(--color-primary), 0.05);
}

.container__dropdowncheckboxfk__dropdown__options__item.option-active {
	background-color: rgba(var(--color-primary), 0.15);
}

.container__dropdowncheckboxfk__dropdown__options__item__check {
	width: 40px;
	height: 40px;
	margin-right: 1rem;
}

.container__dropdowncheckboxfk__dropdown__options__item__group__perpropertie {
	display: flex;
	align-items: center;
}

.container__dropdowncheckboxfk__dropdown__options__item__group__perpropertie__propname {
	margin: 0;
	margin-right: 0.5rem;
	line-height: 1.3rem;
	font-weight: 700;
}

.container__dropdowncheckboxfk__dropdown__options__item__group__perpropertie__propname::after {
	content: ':';
}

.container__dropdowncheckboxfk__selectedoptions.disabled {
	opacity: 40%;
	cursor: not-allowed;
}
</style>
